import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  getConfig, startRun, getEvents, listRuns, interruptRun, pauseRun, resumeRun,
  injectMessage, continueRun, isTerminal, isLive, isContinuable,
} from './agentApi';

/**
 * One run, held above the router, so two surfaces can show it.
 *
 * ### Why the state cannot live in the page
 *
 * The agent acts on this app, and the reason to watch it is to see it happen —
 * on the screen it is changing. A run owned by `pages/Assistant.jsx` ends the
 * moment you navigate away from it: the interval is torn down, the transcript
 * is garbage, and the only way to see what it did is to go back and find
 * nothing there. So the run lives here, mounted above the inner `<Routes>`,
 * and the dock and the page are both views onto it.
 *
 * That is also the cheapest possible version of this, because of a decision
 * already made in `agentApi`: the event stream is cursored on an ID. Polling
 * is "what happened after this id", so a remount, a slow response or a
 * dropped one loses nothing and needs no replay logic.
 *
 * ### A live run survives a reload
 *
 * The run is server-side; the page is not. After a refresh the agent is still
 * working and the UI knew nothing about it, which is the same failure as
 * navigating away. So on mount the newest run is adopted if it is still going
 * — with `after=0`, which replays the whole transcript rather than starting
 * the story in the middle.
 *
 * A terminal run is NOT adopted. Reopening the app should not reinstate the
 * thing you finished with last night as though it were news.
 *
 * ### It does nothing without the permission
 *
 * Both agent surfaces are behind `ai_control`. The provider wraps the whole
 * authenticated app, so it checks for itself rather than trusting its mount
 * point — a non-admin fires no `/agent` request at all, which is worth more
 * than a hidden button.
 */

const POLL_MS = 1500;

const AgentRunContext = createContext(null);

export const useAgentRun = () => useContext(AgentRunContext);

export const AgentRunProvider = ({ children }) => {
  const permissions = useSelector((state) => state.auth?.permissions || []);
  const allowed =
    permissions.includes('admin') || permissions.includes('ai_control');

  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [run, setRun] = useState(null);
  const [events, setEvents] = useState([]);
  const [pending, setPending] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const cursor = useRef(0);

  useEffect(() => {
    if (!allowed) return;
    getConfig().then(setConfig).catch(() => setConfig({ configured: false }));
  }, [allowed]);

  const poll = useCallback(async (runId) => {
    try {
      const res = await getEvents(runId, cursor.current);
      if (res.events?.length) {
        cursor.current = res.last_event_id;
        setEvents((prev) => [...prev, ...res.events]);
      }
      setRun(res.run);
      if (isTerminal(res.run)) setPending([]);
      return res.run;
    } catch (e) {
      setError(e?.message || 'Lost contact with the run');
      return null;
    }
  }, []);

  // One request does two jobs: it adopts a run that is still going, and it is
  // the conversation history. `GET /agent/runs` returns your 25 most recent
  // runs, so there was never a second endpoint to add — only a list to show.
  //
  // Deliberately not chained to the config fetch: a deployment with no API key
  // has no runs to adopt, and waiting on that answer would delay the one case
  // this exists for.
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    listRuns()
      .then((res) => {
        if (cancelled) return;
        setHistory(res?.runs || []);
        const newest = res?.runs?.[0];
        if (!newest || isTerminal(newest)) return;
        cursor.current = 0;
        setRun(newest);
        // The transcript arrives from the polling effect below, which fires
        // as soon as there is a run id — no need to fetch it here as well.
      })
      .catch(() => { /* nothing to resume is the normal case */ });
    return () => { cancelled = true; };
  }, [allowed, poll]);

  // One interval, re-armed when the run id or its liveness changes. A terminal
  // run arms nothing, so an idle tab makes no requests.
  //
  // It polls once immediately rather than waiting out the first interval: a
  // run that has just been started, or just been adopted after a reload,
  // otherwise shows "Working…" with an empty transcript for a second and a
  // half, which reads as nothing having happened.
  useEffect(() => {
    if (!run?.id || isTerminal(run)) return undefined;
    poll(run.id);
    const timer = setInterval(() => poll(run.id), POLL_MS);
    return () => clearInterval(timer);
  }, [run?.id, run?.status, poll]);

  const refreshHistory = useCallback(async () => {
    try {
      const res = await listRuns();
      setHistory(res?.runs || []);
    } catch (e) {
      /* the list is a convenience; failing to refresh it changes nothing */
    }
  }, []);

  const start = useCallback(async ({ instruction, readOnly = false }) => {
    if (!instruction.trim()) return null;
    setBusy(true);
    setError('');
    try {
      const res = await startRun({ instruction, readOnly });
      if (!res?.run) {
        // `configured: false` comes back as a 200 with no run — the feature is
        // off, which is not an error to render as one.
        setError('The agent is not configured on this deployment.');
        return null;
      }
      cursor.current = 0;
      setEvents([]);
      setPending([]);
      setRun(res.run);
      refreshHistory();
      return res.run;
    } catch (e) {
      setError(e?.message || 'Could not start the run');
      return null;
    } finally {
      setBusy(false);
    }
  }, [refreshHistory]);

  const act = useCallback(async (fn, label) => {
    if (!run?.id) return;
    setBusy(true);
    try {
      const res = await fn(run.id);
      if (res?.run) setRun(res.run);
      await poll(run.id);
    } catch (e) {
      setError(e?.message || `Could not ${label}`);
    } finally {
      setBusy(false);
    }
  }, [run?.id, poll]);

  const send = useCallback(async (text) => {
    const body = text.trim();
    if (!body || !run?.id) return false;
    setBusy(true);
    try {
      const res = await injectMessage(run.id, body);
      // Shown as pending, not as sent. See agentApi's note on injection.
      if (res?.injection) setPending((p) => [...p, res.injection]);
      return true;
    } catch (e) {
      setError(e?.message || 'Could not queue that message');
      return false;
    } finally {
      setBusy(false);
    }
  }, [run?.id]);

  /**
   * Open a past conversation.
   *
   * Polls once directly rather than leaning on the interval effect, because a
   * finished run arms no interval — so without this, opening one from the
   * history list would show an empty transcript for ever.
   */
  const openRun = useCallback(async (runId) => {
    cursor.current = 0;
    setEvents([]);
    setPending([]);
    setError('');
    setRun(null);
    await poll(runId);
  }, [poll]);

  /**
   * Say something else to a finished run.
   *
   * Events are NOT cleared: the reply belongs to the same conversation, and
   * the cursor keeps counting, so the new leg appends to the transcript
   * already on screen.
   */
  const carryOn = useCallback(async (text) => {
    const body = text.trim();
    if (!body || !run?.id) return false;
    setBusy(true);
    setError('');
    try {
      const res = await continueRun(run.id, body);
      if (res?.run) setRun(res.run);
      return true;
    } catch (e) {
      setError(e?.message || 'Could not continue that run');
      return false;
    } finally {
      setBusy(false);
    }
  }, [run?.id]);

  const reset = useCallback(() => {
    setRun(null);
    setEvents([]);
    setPending([]);
    setError('');
    cursor.current = 0;
  }, []);

  const value = useMemo(() => ({
    allowed,
    config,
    run,
    events,
    pending,
    error,
    busy,
    history,
    refreshHistory,
    openRun,
    start,
    send,
    carryOn,
    reset,
    interrupt: () => act(interruptRun, 'stop'),
    pause: () => act(pauseRun, 'pause'),
    resume: () => act(resumeRun, 'resume'),
    // Derived once here rather than recomputed in each surface, so the dock
    // and the page cannot disagree about whether a run is stopping.
    live: isLive(run),
    terminal: isTerminal(run),
    stopping: run?.control === 'interrupt' && !isTerminal(run),
    pausing: run?.control === 'pause' && run?.status !== 'paused',
    // `failed` is terminal but not continuable, so the UI has to tell them
    // apart rather than offering a box that 409s.
    continuable: isContinuable(run),
  }), [allowed, config, run, events, pending, error, busy, history,
       refreshHistory, openRun, start, send, carryOn, reset, act]);

  return (
    <AgentRunContext.Provider value={value}>{children}</AgentRunContext.Provider>
  );
};

export default AgentRunProvider;
